<template>
  <div>
    <canvas ref="chart" class="f-h f-w" />
  </div>
</template>

<script>
/**
 * GW Checklist item chart
 *
 * @vue-prop {String} [type='line'] - Type of chart drawn. Can be 'line' or 'bar'
 * @vue-prop {Object} [statsData] - Raw stats data given the server
 * @vue-prop {Array} [clItems=[0,1,2]] - Checklist items type to display (NA, NOK, OK)
 * @vue-prop {Array} [workingDays=[true...]] - Array where the index is the weekday and
 *                   the value is whether the day should be displayed or not
 * @vue-prop {Boolean} [hideNonWorkingDays=false] - Whether these days should be hidden or only greyed out
 * @vue-prop {Object} visibleChartRange - Range of <b>visible</b> values in the chart. Same as above.
 */

import Moment from 'moment-timezone';
import { Chart } from 'chart.js';
// Import utils
import { isEqual } from 'lodash';
import { CL_ITEMS_CHART_TYPES } from '@/utils/gw-stats';
import { ChartHelper, formatConfig } from '@/utils/indicator-utils';
import { SColors } from '@/utils/constants';

export default {

  name: 'GwChecklistItemsChart',

  props: {
    type: {
      type: String,
      default: CL_ITEMS_CHART_TYPES[0].type,
    },
    statsData: {
      type: Object,
      default: () => ({}),
    },
    clItems: {
      type: Array,
      default: () => ([0, 1, 2]),
    },
    workingDays: {
      type: Array,
      default: () => [true, true, true, true, true, true, true],
    },
    visibleChartRange: {
      type: Object,
      validator(value) {
        return value instanceof Object
          && (Moment.isMoment(value.start) && value.start.isValid())
          && (Moment.isMoment(value.end) && value.end.isValid());
      },
      default: null,
    },
    hideNonWorkingDays: Boolean,
    displayConfig: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      chart: null,
      chartHelper: null,
    };
  },

  computed: {
    chartData() {
      if (!this.chartHelper) {
        return null;
      }
      const datasets = [];
      const isLine = this.type === CL_ITEMS_CHART_TYPES[0].type;
      const commonDataset = {
        type: this.type,
        borderWidth: isLine ? 2 : 0.1, // 0 does not work
        hoverBorderWidth: 0,
        pointBorderWidth: 0.1,
        tension: 0,
        fill: '-1',
        pointRadius: 3,
        pointHitRadius: 40,
        pointHoverRadius: 7,
        maxBarThickness: 8,
        borderRadius: 200,
      };
      // Transform the values to be compatible with buildCustomConfigChart
      const okValues = {};
      const nokValues = {};
      const naValues = {};
      Object.keys(this.statsData).forEach((dateKey) => {
        const [nokValue, okValue, naValue] = this.statsData[dateKey];
        okValues[dateKey] = { reported: dateKey, value: okValue };
        nokValues[dateKey] = { reported: dateKey, value: nokValue };
        naValues[dateKey] = { reported: dateKey, value: naValue };
      });

      const { chartHelper: ch, workingDays, visibleChartRange: range } = this;
      const formattedConfig = formatConfig(this.displayConfig);
      // Let the chartHelper remove the non working days if the option is set
      const fakeIndic = { workingDays: this.hideNonWorkingDays ? workingDays : undefined };
      // Build points according to the chart config
      const okPoints = ch.buildCustomConfigChart(fakeIndic, okValues, formattedConfig, workingDays, range).points;
      const nokPoints = ch.buildCustomConfigChart(fakeIndic, nokValues, formattedConfig, workingDays, range).points;
      const naPoints = ch.buildCustomConfigChart(fakeIndic, naValues, formattedConfig, workingDays, range).points;

      const okPointValues = okPoints.map(p => p.data);
      const nokPointValues = nokPoints.map(p => p.data);
      const naPointValues = naPoints.map(p => p.data);
      const totalPointValues = okPoints.map((o, idx) => okPointValues[idx] + nokPointValues[idx] + naPointValues[idx]);

      if (this.clItems.includes(0)) {
        datasets.push(Object.assign({
          values: naPointValues, // Number of checklist items corresponding to this state
          label: this.$t('message.GW_NOT_APPLICABLE'),
          backgroundColor: isLine ? SColors.mediumLightGray : SColors.gray,
          borderColor: SColors.mediumGray,
          pointBackgroundColor: SColors.mediumGray,
          data: naPointValues.map((val, idx) => val * 100 / totalPointValues[idx]), // Percentages
          stack: 'stackedData',
          yAxisID: 'y',
        }, commonDataset));
      }
      if (this.clItems.includes(1)) {
        datasets.push(Object.assign({
          values: nokPointValues,
          label: this.$t('message.GW_NOK_ITEMS'),
          backgroundColor: isLine ? SColors.indicatorsRedBg : SColors.indicatorsRedLine,
          borderColor: SColors.indicatorsRedLine,
          pointBackgroundColor: SColors.indicatorsRedLine,
          data: nokPointValues.map((val, idx) => val * 100 / totalPointValues[idx]),
          stack: 'stackedData',
          yAxisID: 'y',
        }, commonDataset));
      }
      if (this.clItems.includes(2)) {
        datasets.push(Object.assign({
          values: okPointValues,
          label: this.$t('message.GW_OK_ITEMS'),
          backgroundColor: isLine ? SColors.indicatorsGreenBg : SColors.indicatorsGreenLine,
          borderColor: SColors.indicatorsGreenLine,
          pointBackgroundColor: SColors.indicatorsGreenLine,
          data: okPointValues.map((val, idx) => val * 100 / totalPointValues[idx]),
          stack: 'stackedData',
          yAxisID: 'y',
        }, commonDataset));
      }
      if (datasets.length > 0) {
        datasets[0].fill = 'start';
      }

      return { datasets, labels: ch.getLabelsFromChartPoints(okPoints).labels };
    },
    /** If non-working days aren't hidden, a gray backgrund is displayed over these days */
    bgDataset() {
      if (!this.hideNonWorkingDays) {
        const dataset = {
          label: '',
          borderWidth: 0,
          tension: 0,
          fill: 'origin',
          backgroundColor: 'rgba(0, 0, 0, 0.07)',
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
          values: [],
          data: [],
          barThickness: 'flex',
          categoryPercentage: 1,
          barPercentage: 1,
          stack: 'stackedData',
          yAxisID: 'backgroundY',
        };
        Object.keys(this.statsData).forEach((key) => {
          const date = this.$moment(key);
          dataset.data.push(this.workingDays[date.isoWeekday() - 1] ? 0 : 100);
        });
        return dataset;
      }
      return null;
    },
    /** Main data + bgDataset */
    allChartData() {
      if (!this.chartData) {
        return null;
      }
      return {
        datasets: this.hideNonWorkingDays
          ? this.chartData.datasets
          : [this.bgDataset, ...this.chartData.datasets],
        labels: this.chartData.labels,
      };
    },
  },

  watch: {
    type() {
      this.createChart();
    },
    allChartData(newData) {
      this.chart.data = newData;
      this.chart.update();
    },
    hideNonWorkingDays() {
      this.createChart();
    },
    workingDays(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.createChart();
      }
    },
  },

  created() {
    this.chartHelper = new ChartHelper();
  },

  mounted() {
    this.createChart();
  },

  methods: {
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    /**
     * Create and mount the stats chart ; Will destroy previous chart
     */
    createChart() {
      const canvas = this.$refs.chart;
      if (!canvas) {
        return;
      }
      if (this.chart) {
        this.chart.destroy();
      }
      const context = canvas.getContext('2d');
      this.chart = new Chart(context, {
        type: 'bar',
        data: this.allChartData,
        options: {
          spanGaps: true,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              stacked: true,
              beginAtZero: true,
              max: 103,
              ticks: {
                callback(label) {
                  return (label % 10 === 0) ? label : null;
                },
              },
              grid: {
                color: SColors.grayBgHover,
                borderDash: [3, 3],
                tickColor: 'rgba(0, 0, 0, 0)',
                borderColor: 'rgba(0, 0, 0, 0)',
              },

            },
            x: {
              grid: {
                color: 'transparent',
                drawTicks: false,
              },
            },
            backgroundY: {
              beginAtZero: true,
              max: 103,
              display: false,
              stacked: false,
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: (item) => {
                  if (!this.hideNonWorkingDays && item.datasetIndex === 0) {
                    return this.$t('message.GW_NON_WD');
                  }
                  const dateIdx = item.dataIndex;
                  const value = item.dataset.values[dateIdx];
                  const perc = item.dataset.data[dateIdx];
                  return `${value} ${item.dataset.label} (${Number.isInteger(perc) ? perc : perc.toFixed(1)}%)`;
                },
              },
            },
          },
        },
      });
    },
  },
};
</script>
